import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useBranch } from '../contexts/BranchContext';

const STEP_RESTAURANT = 'restaurant';
const STEP_BRANCH = 'branch';

const BranchSelectModal = () => {
  const {
    restaurants,
    allBranches,
    selectedRestaurant,
    selectedBranch,
    selectRestaurant,
    selectBranch,
    branchSelectModalVisible,
    setBranchSelectModalVisible,
    isLoading,
  } = useBranch();

  const [step, setStep] = useState(STEP_RESTAURANT);
  const [pickedRestaurant, setPickedRestaurant] = useState(null);

  useEffect(() => {
    if (branchSelectModalVisible) {
      setStep(STEP_RESTAURANT);
      setPickedRestaurant(null);
    }
  }, [branchSelectModalVisible]);

  const handleSelectRestaurant = (restaurant) => {
    setPickedRestaurant(restaurant);
    setStep(STEP_BRANCH);
  };

  const handleSelectBranch = (branch, restaurant) => {
    selectRestaurant(restaurant);
    selectBranch(branch);
    setBranchSelectModalVisible(false);
  };

  const goBack = () => {
    setStep(STEP_RESTAURANT);
    setPickedRestaurant(null);
  };

  const closeModal = () => {
    setBranchSelectModalVisible(false);
  };

  const restaurantList = React.useMemo(() => {
    if (!restaurants?.length) return [];
    if (!allBranches?.length) return restaurants;
    const restaurantIds = new Set(
      allBranches.map((b) => b.restaurant?._id || b.restaurant).filter(Boolean)
    );
    return restaurants.filter((r) => restaurantIds.has(r._id));
  }, [restaurants, allBranches]);

  const branchesForPickedRestaurant = React.useMemo(() => {
    if (!pickedRestaurant || !allBranches?.length) return [];
    const rid = pickedRestaurant._id;
    return allBranches.filter(
      (b) => b.restaurant?._id === rid || b.restaurant === rid
    );
  }, [pickedRestaurant, allBranches]);

  const visible = branchSelectModalVisible && (restaurantList.length >= 1 || step === STEP_BRANCH);

  if (!visible) return null;

  return (
    <Dialog open={visible} onClose={closeModal} maxWidth="sm" fullWidth>
      {step === STEP_RESTAURANT ? (
        <>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Select restaurant</Typography>
              <Typography variant="body2" color="text.secondary">Choose a restaurant first</Typography>
            </Box>
            <IconButton onClick={closeModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : restaurantList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <RestaurantIcon sx={{ fontSize: 40, color: '#94A3B8', mb: 1 }} />
                <Typography color="text.secondary">No restaurants found</Typography>
              </Box>
            ) : (
              <List>
                {restaurantList.map((restaurant, index) => {
                  const isSelected = selectedRestaurant?._id === restaurant._id;
                  return (
                    <React.Fragment key={restaurant._id}>
                      {index > 0 && <Box sx={{ height: 1, bgcolor: '#E2E8F0', my: 1 }} />}
                      <ListItemButton
                        onClick={() => handleSelectRestaurant(restaurant)}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          bgcolor: isSelected ? '#FDF2F4' : 'transparent',
                        }}
                      >
                        <ListItemIcon>
                          <RestaurantIcon sx={{ color: isSelected ? '#851E41' : '#64748B' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={restaurant?.name || 'Restaurant'}
                          primaryTypographyProps={{
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? '#851E41' : '#1F2937',
                          }}
                        />
                      </ListItemButton>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={goBack} size="small" sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Select branch</Typography>
              <Typography variant="body2" color="text.secondary">{pickedRestaurant?.name || 'Restaurant'}</Typography>
            </Box>
            <IconButton onClick={closeModal} size="small" sx={{ ml: 'auto' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {branchesForPickedRestaurant.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <StoreIcon sx={{ fontSize: 40, color: '#94A3B8', mb: 1 }} />
                <Typography color="text.secondary">No branches found</Typography>
              </Box>
            ) : (
              <List>
                {branchesForPickedRestaurant.map((branch, index) => {
                  const isSelected = selectedBranch?._id === branch._id && selectedRestaurant?._id === pickedRestaurant?._id;
                  return (
                    <React.Fragment key={branch._id}>
                      {index > 0 && <Box sx={{ height: 1, bgcolor: '#E2E8F0', my: 1 }} />}
                      <ListItemButton
                        onClick={() => handleSelectBranch(branch, pickedRestaurant)}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          bgcolor: isSelected ? '#FDF2F4' : 'transparent',
                        }}
                      >
                        <ListItemIcon>
                          <StoreIcon sx={{ color: isSelected ? '#851E41' : '#64748B' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={branch?.name || 'Unnamed Branch'}
                          primaryTypographyProps={{
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? '#851E41' : '#1F2937',
                          }}
                        />
                        {isSelected && <CheckCircleIcon sx={{ color: '#851E41' }} />}
                        <Box component="span" sx={{ ml: 1, fontSize: 12, color: branch?.isOperational ? '#10B981' : '#EF4444' }}>
                          {branch?.isOperational ? 'Open' : 'Closed'}
                        </Box>
                      </ListItemButton>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default BranchSelectModal;